import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { LuCircleStop, LuMonitor, LuMapPin, LuGlobe, LuNetwork, LuPlus } from 'react-icons/lu'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import Modal from './Modal'
import NameForm from './NameForm'
import CreateRefusal from './CreateRefusal'
import useAuth from '../models/useAuth'
import { readNodesAgain } from '../models/useNode'
import useNetwork from '../models/useNetwork'
import useNodeMetadata from '../models/useNodeMetadata'
import { getOrigin } from '../lib/origin'
import { getUserId } from '../lib/workos'
import { fetchAttachFacts, type AttachFacts } from '../lib/registerAttach'
import { readNodeOnline } from '../lib/nodeOnline'
import { addableSession, hiddenNetworksLine, noAddableNetworkLine } from '../utilities/registerNetworks'
import {
  createNetwork,
  denyRegisterSession,
  fetchNameCheck,
  fetchRegisterPreview,
  fetchRegisterSession,
  loginUrlFor,
  submitRegisterApproval,
} from '../lib/register'
import { limitRefusal, type LimitRefusal } from '../utilities/limitRefusal'
import { NETWORK_ACCESS_LABEL, keepPrivateCaption } from '../utilities/nodeAccess'
import { useNetworkAccess } from '../hooks/useNetworkAccess'
import {
  CANCELLED_MESSAGE,
  REGISTER_POLL_MS,
  REGISTER_WAIT_LIMIT_MS,
  approvalBody,
  effectiveNodeName,
  isCurrentCheck,
  nameBlocksApproval,
  nameNoticeView,
  replaceChosen,
  type NameCheck,
  type NameNotice,
  type OfflineChoice,
  byId,
  canApprove,
  defaultNetworkId,
  defaultNodeName,
  defaultOrgId,
  expiredMessage,
  isApproved,
  isSessionId,
  isTerminal,
  isValidNodeName,
  landingTitle,
  locationLine,
  machineLine,
  nodePagePath,
  opensNode,
  REGISTER_CONNECT_WAIT_MS,
  plannedNetwork,
  registerPhase,
  selectedNetworkName,
  showsNetworkPicker,
  showsOrgPicker,
  waitedLabel,
  type RegisterFailure,
  type RegisterPreview,
  type RegisterSelection,
  type RegisterSession,
} from '../utilities/registerSession'

/**
 * The jump to the router's login, remembered across it.
 *
 * `sessionStorage` is per tab and dies with it, which is the right life for "I have already sent
 * this reader to sign in once". A browser that refuses storage — the same private mode that
 * refuses the cookies this guards against — reads as "not spent", so the page still works and only
 * loses the sentence.
 */
const bounceKey = (sessionId: string) => `nobgp-register-bounce-${sessionId}`

const bounceSpent = (sessionId: string): boolean => {
  try {
    return window.sessionStorage.getItem(bounceKey(sessionId)) !== null
  } catch {
    return false
  }
}

const markBounce = (sessionId: string) => {
  try {
    window.sessionStorage.setItem(bounceKey(sessionId), '1')
  } catch {
    // Nothing to do: the guard is a convenience, and the jump matters more than remembering it.
  }
}

const forgetBounce = (sessionId: string | null) => {
  if (!sessionId) return
  try {
    window.sessionStorage.removeItem(bounceKey(sessionId))
  } catch {
    // As above.
  }
}

/**
 * `/register` — the browser leg of a node registration (nobgp-router#1603).
 *
 * The agent prints `app.nobgp.com/register` and a code, and its QR code carries the code
 * (nobgp-router#1859). The code step (`CliCode.tsx`) finds the session and comes here with
 * `?session=<uuid>`, which is this page's own address and never a printed one. The reader
 * is often on a phone, and the machine is a headless box in another room. So this page, and not
 * the terminal, is where the machine is approved: it says what is being registered, signs the
 * reader in, lets them pick the org and the network (or create one) and name the machine, and
 * then waits for the machine and lands on the new node's page.
 *
 * ⚠ The page is NOT under `AuthGuard`. A reader with no session sees a landing card first — the
 * unauthenticated preview says which machine this is — and then goes to the ROUTER's
 * `/auth/login?auth_session=…`, because that leg gives the machine a token. It is no longer the
 * only one: since router#1609 the APPROVAL gives the machine the approving user's token too, which
 * is what lets a reader who was already signed in approve without stranding it. The app's own
 * `/login` would still skip the session binding, so the jump stays the router's. See
 * `registerLoginUrl`.
 *
 * ⚠ The jump is a BUTTON now, not automatic (Benoit, 2026-09-17: show what is being registered
 * first). That also ends the loop the automatic jump needed a guard against: a sign-in that does
 * not stick — cookies blocked on a phone browser — returns the reader to this card, which then
 * says so, instead of to another jump.
 *
 * ⚠⚠ The poll stops only on a failure no later read can change — see `isTerminal`. The first
 * version stopped on less, and gave up on a machine that came online.
 *
 * The rules are in `utilities/registerSession.ts`, with tests, and `npm run harness` renders every
 * state at `?panel=register`.
 */
interface Props {
  /**
   * How long the page waits before it says the machine is taking longer than expected. Only the
   * harness passes one — `&state=overdue` cannot wait two real minutes for the card it measures.
   */
  waitLimitMs?: number
}

/** How long the name must sit still before the page asks whether it is taken. */
const NAME_CHECK_DEBOUNCE_MS = 300

const EMPTY_SELECTION: RegisterSelection = { orgId: '', networkId: '', networkName: '', nodeName: '' }

/**
 * A read of the registration that failed while the page keeps asking (app#469).
 *
 * ⚠ A moving spinner, always. Every failure that reaches this notice is one the poll goes on from
 * (`isTerminal` stops the poll, and a terminal failure has a card of its own), so the page IS
 * trying again, whatever the message says. Before app#469 the theme's info icon was a loader
 * glyph, which did not move, and which every plain note had too.
 */
function ReadNotice({ failure }: Readonly<{ failure: RegisterFailure }>) {
  return (
    <Alert severity="info" icon={<CircularProgress size="1rem" />}>
      {failure.message}
    </Alert>
  )
}

export default function Register({ waitLimitMs = REGISTER_WAIT_LIMIT_MS }: Readonly<Props>) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const sessionId = searchParams.get('session')
  const validLink = isSessionId(sessionId)

  const initialized = useAuth(state => state.initialized)
  const session = useAuth(state => state.session)
  const signedIn = !!session

  const [preview, setPreview] = useState<RegisterPreview | null>(null)
  const [record, setRecord] = useState<RegisterSession | null>(null)
  const [failure, setFailure] = useState<RegisterFailure | null>(null)
  const [sendFailure, setSendFailure] = useState<RegisterFailure | null>(null)
  const [selection, setSelection] = useState<RegisterSelection>(EMPTY_SELECTION)
  const [sending, setSending] = useState(false)
  // The reader selected Deny and the router took it: the page ends here.
  const [denied, setDenied] = useState(false)
  // A Deny in flight. Not `sending`, which is the approval: the Approve button would read
  // "Registering…" while the reader denies.
  const [denying, setDenying] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  // ⚠ THIS PAGE SENT THE APPROVAL, which is not the same fact as the record carrying one: the
  // router answers `204` with no body, and a poll already in flight answers with the record as it
  // was before. Either one would otherwise put the approve form back in front of the reader.
  const [approved, setApproved] = useState(false)

  const [createOpen, setCreateOpen] = useState(false)
  const [newNetwork, setNewNetwork] = useState('')
  const [creating, setCreating] = useState(false)
  const [refusal, setRefusal] = useState<Pick<LimitRefusal, 'message' | 'upgradeUrl'> | null>(null)
  // Who may add a node to each listed network (app#397), and a counter that reads it again after
  // the router refused an approval for that reason.
  const [attach, setAttach] = useState<AttachFacts | null>(null)
  const [attachReads, setAttachReads] = useState(0)

  // A name that is already taken (Benoit, router#1603). `notice` is the last conflict the page saw
  // and outlives the pre-fill, so the reader sees why the field changed; `offlineChoice` is the
  // reader's answer to an offline name, and nothing sets it but a click.
  const [notice, setNotice] = useState<NameNotice | null>(null)
  const [offlineChoice, setOfflineChoice] = useState<OfflineChoice>('')
  // The names this page already pre-filled over, per network. The page pre-fills ONCE per name: a
  // reader who types the taken name back has decided, and the approval's own check answers them.
  const prefilled = useRef(new Set<string>())

  // The reader's own edit wins over every default, and over every later read of the record.
  // Without this the poll would type over them every few seconds.
  const edited = useRef({ org: false, network: false, name: false })
  // When the approval reached the record. A reload after the approval has no start time of its
  // own, so the first read that carries one becomes the start.
  const choiceSeenAt = useRef<number | null>(null)

  const waitedMs = choiceSeenAt.current === null ? 0 : now - choiceSeenAt.current
  const phase = registerPhase(record, waitedMs, waitLimitMs, approved)
  const terminal = isTerminal(failure)
  // ⚠ A 401 from the router is a signed-in page holding a token the router will not take. It goes
  // back to the landing card and its sign-in button, like a reader with no session at all.
  const stale = failure?.kind === 'unauthenticated'
  const needsSignIn = initialized && (!signedIn || stale)

  const pick = (change: Partial<RegisterSelection>) => setSelection(current => ({ ...current, ...change }))

  // The name the router will use, in the network it will join. The check needs an id, so on an
  // older router (flat names) there is no check, and the approval's own check is the only one.
  const checkNetworkId = record && byId(record) ? selection.networkId : ''
  const checkName = record ? effectiveNodeName(record, selection.nodeName) : ''
  const nameView = nameNoticeView(notice, checkName, checkNetworkId, offlineChoice)
  // The picked network's access, Org or Private (you and org admins), shown beside "Keep this node
  // private" (D-20). null on an older router (no id) or when it cannot be read: the pane then says
  // what each value does, without naming this network's.
  const [networkAccess] = useNetworkAccess(checkNetworkId)
  const latest = useRef({ name: checkName, networkId: checkNetworkId })
  latest.current = { name: checkName, networkId: checkNetworkId }

  // A conflict, from the check or from the approval's 422: remember it, and pre-fill the free name
  // for an ONLINE holder. An offline holder gets a choice instead, and none is made for the reader.
  const noticeRef = useRef<NameNotice | null>(null)
  noticeRef.current = notice
  const applyConflict = useCallback((check: NameCheck) => {
    const current = noticeRef.current
    const sameName = !!current && current.name === check.name && current.networkId === check.networkId
    if (check.status === 'free') {
      // The holder went away. The notice about THIS name goes with it; a notice about the name the
      // page pre-filled over stays, because it explains the field.
      if (sameName) setNotice(null)
      return
    }
    // A new conflict clears the old answer: a choice made about another name, or about the same
    // name when its holder was in another state, is not a choice about this one.
    if (!sameName || current?.status !== check.status) setOfflineChoice('')
    setNotice({ name: check.name, networkId: check.networkId, status: check.status, suggested: check.suggested })
    const key = `${check.networkId}/${check.name}`
    // Online and unavailable pre-fill; offline asks. ONCE per name, as above.
    if (check.status !== 'offline' && check.suggested && !prefilled.current.has(key)) {
      prefilled.current.add(key)
      edited.current.name = true
      setSelection(prev => ({ ...prev, nodeName: check.suggested ?? prev.nodeName }))
    }
  }, [])

  // The check, debounced, for the name and network the reader has now. ⚠ A slow answer about a name
  // the reader has since changed is dropped twice over: the request is aborted when either input
  // moves, and an answer is applied only if it is still about what is on screen.
  useEffect(() => {
    if (!sessionId || !signedIn || phase !== 'approve' || !checkNetworkId || !isValidNodeName(checkName) || !checkName)
      return
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      const result = await fetchNameCheck(sessionId, checkNetworkId, checkName, controller.signal)
      if (controller.signal.aborted || result.kind !== 'checked') return
      if (!isCurrentCheck(result.check, latest.current.name, latest.current.networkId)) return
      applyConflict(result.check)
    }, NAME_CHECK_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [sessionId, signedIn, phase, checkNetworkId, checkName, applyConflict])

  const read = useCallback(async () => {
    if (!sessionId) return
    const result = await fetchRegisterSession(sessionId)
    setNow(Date.now())
    if (!result.ok) {
      setFailure(result.failure)
      return
    }
    // ⚠ A read the router ANSWERED is the only proof the sign-in worked, and it is what clears the
    // "sign-in did not finish" mark. Clearing it merely because the app holds a session would hide
    // the sentence from a reader whose token the router refuses.
    forgetBounce(sessionId)
    setFailure(null)
    setRecord(result.value)
  }, [sessionId])

  // ⚠⚠ ONLY THE NETWORKS THE READER MAY ADD A NODE TO (docs/PERMISSIONS.md § 5 Attachment, app#397).
  // A network whose "members may add nodes" is off takes a new node only from its owner or an org
  // Owner/Admin, and the router refuses anyone else at the approval. The page reads the settings
  // and the reader's roles once per SET of listed networks (not once per poll), and `offered` is
  // the record with the others left out. Every rule below that picks or counts networks reads
  // `offered`, so a network the reader may not use is never preselected or counted. A fact the page
  // cannot read keeps the network listed: the router stays the gate.
  const listedIds = useMemo(
    () => (record ? record.orgs.flatMap(org => org.networks.map(network => network.id)).sort().join(',') : ''),
    [record]
  )
  useEffect(() => {
    if (!signedIn || !listedIds) return
    let cancelled = false
    fetchAttachFacts(listedIds.split(','), getUserId()).then(result => {
      if (!cancelled) setAttach(result)
    })
    return () => {
      cancelled = true
    }
  }, [signedIn, listedIds, attachReads])
  const view = useMemo(
    () =>
      record
        ? attach
          ? addableSession(record, attach.networks, attach.roles, getUserId())
          : { session: record, hidden: {} as Record<string, string[]> }
        : null,
    [record, attach]
  )
  const offered = view?.session ?? null

  // Before sign-in: what is being registered. The preview binds nothing, so it is safe to read
  // with no session; only the authenticated GET below claims the registration for this reader.
  useEffect(() => {
    if (!validLink || !needsSignIn || !sessionId) return
    let cancelled = false
    fetchRegisterPreview(sessionId).then(result => {
      if (!cancelled) setPreview(result)
    })
    return () => {
      cancelled = true
    }
  }, [validLink, needsSignIn, sessionId])

  // ⚠ The tokens ride back in the fragment (router `HandleCallback`), and only `/auth/callback`
  // strips them. Nothing here reads them — the session comes from the router's cookies — so they
  // are cleared rather than left in the address bar of a page a reader keeps open, and on a phone
  // hands to somebody else to look at.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location.hash.includes('access_token')) return
    window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`)
  }, [])

  // One poll for the whole page: it brings the facts the reader approves, and then watches for the
  // machine. It stops only on a terminal failure and when the node exists.
  useEffect(() => {
    if (!validLink || !signedIn || stale || terminal || denied || phase === 'done' || phase === 'cancelled') return
    read()
    const timer = setInterval(read, REGISTER_POLL_MS)
    return () => clearInterval(timer)
  }, [validLink, signedIn, stale, terminal, denied, phase, read])

  // Defaults, from each read. The router proposes the machine's hostname as the name;
  // `defaultNodeName` is the fallback for a router that sends no proposal.
  useEffect(() => {
    if (!offered) return
    setSelection(current => {
      const next = { ...current }
      // ⚠⚠ THE PROPOSAL IS A HOSTNAME AND NOT YET A NODE NAME (`my_nas`, `Benoits-MBP`). A node
      // name is a DNS label, so `defaultNodeName` — the rule the agent applies — cleans it first,
      // and leaves a name that is already valid alone.
      if (!edited.current.name)
        next.nodeName = defaultNodeName(offered.nodeName ?? offered.suggestedName ?? offered.hostname)
      if (byId(offered)) {
        const orgKnown = offered.orgs.some(o => o.id === next.orgId)
        if (!edited.current.org || !orgKnown) next.orgId = orgKnown ? next.orgId : defaultOrgId(offered)
        const org = offered.orgs.find(o => o.id === next.orgId)
        const networkKnown = !!org?.networks.some(n => n.id === next.networkId)
        if (!edited.current.network || !networkKnown) next.networkId = networkKnown ? next.networkId : defaultNetworkId(offered, next.orgId)
      } else if (!edited.current.network) {
        next.networkName = offered.network ?? plannedNetwork(offered) ?? ''
      }
      return next
    })
    if (isApproved(offered) && choiceSeenAt.current === null) choiceSeenAt.current = Date.now()
  }, [offered])

  // The machine finished. Land on its page when the node is ONLINE.
  //
  // ⚠⚠ WAIT FOR THE NODE, NOT ONLY FOR THE RECORD. The router marks the record registered when it
  // writes the node, before the agent connects, and a replaced node keeps the `offline_at` of the
  // machine it replaced until then. Opened at once, the node page read the node offline (app#366,
  // app#433). So the page reads the node's row until it is online, and opens the page
  // anyway after REGISTER_CONNECT_WAIT_MS (`opensNode`).
  //
  // ⚠ RE-READ THE STORES before it opens (app#366): the realtime events for the node may not have
  // reached this tab (the agent is bringing up a TUN, routes and DNS on this very machine as they
  // are sent). Networks too: a registration may have created one, and that decides whether the
  // node's own events are in scope at all.
  const nodeId = record?.nodeId ?? null
  const nodePath = useMemo(() => nodePagePath(nodeId), [nodeId])
  useEffect(() => {
    if (!nodePath || !nodeId) return
    // `opened` stops a second check once one decided to open; `cancelled` is the page going away.
    let opened = false
    let cancelled = false
    const started = Date.now()
    const check = async () => {
      // ⚠ Past the limit, do not read: a read that hangs (this machine is changing its routes and
      // DNS) would otherwise hold the page past the limit.
      const waited = () => Date.now() - started
      const online = waited() >= REGISTER_CONNECT_WAIT_MS ? null : await readNodeOnline(nodeId)
      if (opened || cancelled || !opensNode(online, waited())) return
      opened = true
      clearInterval(timer)
      // The reloads get a few seconds, not for ever: the node page reads the stores again itself.
      await Promise.race([
        // ⚠ `readNodesAgain`, not a bare node read: with Replace this machine took the id of an
        // offline node, and that node can have services (app#436). It reads them after the node
        // read lands, also when that is after the 5 seconds.
        Promise.all([useNetwork.getState().get(), readNodesAgain()]),
        new Promise(resolve => setTimeout(resolve, 5000)),
      ])
      // `node_metadata` is not live (app#132): a missed `online_at` event also missed its refresh.
      void useNodeMetadata.getState().getOne(nodeId)
      if (!cancelled) navigate(nodePath, { replace: true })
    }
    const timer = setInterval(check, REGISTER_POLL_MS)
    void check()
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [nodePath, nodeId, navigate])

  // `replace` keeps the back button out of a loop between the two origins.
  const signIn = () => {
    const origin = getOrigin()
    if (!origin || !sessionId) return
    markBounce(sessionId)
    window.location.replace(loginUrlFor(sessionId, origin))
  }

  // Deny, as on the sign-in page (Benoit, 2026-09-28): the machine stops waiting, nothing is
  // registered, and the terminal says who denied it. A registration that already produced its node
  // cannot be denied; the read shows it.
  const deny = async () => {
    if (!sessionId) return
    setDenying(true)
    setSendFailure(null)
    const result = await denyRegisterSession(sessionId)
    setDenying(false)
    if (result === 'denied') setDenied(true)
    else if (result === 'registered') void read()
    else setSendFailure({ kind: 'error', message: 'Deny did not go through. Try again.', retryable: true })
  }

  // The approval. `approvalBody` decides between `network_id` and a name.
  const send = async () => {
    if (!sessionId || !record || !canApprove(record, selection) || nameBlocksApproval(nameView)) return
    setSending(true)
    setSendFailure(null)
    const sentName = checkName
    const sentNetworkId = checkNetworkId
    // ⚠ A replace names the node it replaces. With the field blank the router would judge the raw
    // hostname, while the page checked the cleaned one, so the checked name goes in the body.
    const replace = replaceChosen(nameView)
    const sent = replace ? { ...selection, nodeName: checkName } : selection
    const result = await submitRegisterApproval(sessionId, approvalBody(record, sent, replace))
    setSending(false)
    // ⚠ The approval checked the name again and it is taken now — the holder came online, or went
    // offline, after the page last asked. Same notice as the check, the free name pre-filled for an
    // online holder, and the reader approves again. The 422 is FRESH news, so the pre-fill happens
    // even for a name the page pre-filled over before.
    if (!result.ok && result.failure.kind === 'name_taken' && result.failure.conflict) {
      prefilled.current.delete(`${sentNetworkId}/${sentName}`)
      applyConflict({ name: sentName, networkId: sentNetworkId, ...result.failure.conflict })
      setSendFailure(result.failure)
      return
    }
    // ⚠ A `409` says this registration already has a network — a second press, or a first one that
    // crossed the router's own record. The machine is registering either way, so the page waits
    // for it rather than showing the reader a refusal for something that is going well.
    if (!result.ok && result.failure.kind !== 'already') {
      // ⚠⚠ A 404 FROM THE APPROVAL IS NOT PROOF THAT THE RECORD IS GONE. The router also answers
      // `404 unknown network` for a network that went away after the read, and "this account has
      // no network" — both text/plain, so they classify as `expired`. The page says it beside the
      // button and reads again: the read is what knows whether the record expired, and it brings
      // the network lists as they are now.
      if (result.failure.kind === 'expired') {
        setSendFailure({
          kind: 'error',
          message: 'The router cannot find that network. Choose the network again, or create one.',
          retryable: false,
        })
        void read()
        return
      }
      // ⚠ Only a terminal refusal ends the page. Anything else — a name the router refused, a
      // blip — is said beside the button, and the form stays as the reader left it.
      if (isTerminal(result.failure)) setFailure(result.failure)
      else setSendFailure(result.failure)
      // The network's setting may have changed since the page read it: read it again, so the
      // network the router refused leaves the list and the selection goes back to a default.
      if (result.failure.kind === 'attach_refused') setAttachReads(count => count + 1)
      return
    }
    setFailure(null)
    setApproved(true)
    choiceSeenAt.current = Date.now()
    setNow(Date.now())
    // ⚠ Only when the router sent one. `204` means accepted with no body, and the next poll brings
    // the record — replacing it with an empty one would blank the machine's facts on screen.
    if (result.ok && result.value) setRecord(result.value)
  }

  // A new network in the picked org, then a fresh read so the pickers carry it, preselected.
  const create = async () => {
    const name = newNetwork.trim()
    if (!name || !isValidNodeName(name)) return
    setCreating(true)
    setRefusal(null)
    try {
      const networkId = await createNetwork(name, selection.orgId)
      edited.current.network = true
      if (networkId) pick({ networkId })
      pick({ networkName: name })
      setNewNetwork('')
      setCreateOpen(false)
      await read()
    } catch (e) {
      // Same shape as `ModalNetwork`: a spent allowance keeps its Upgrade action, anything else
      // keeps its words, and the typed name stays in the field.
      console.error('network_create failed', e)
      setRefusal(limitRefusal(e) ?? { message: e instanceof Error ? e.message : String(e) })
    } finally {
      setCreating(false)
    }
  }

  const frame = (children: React.ReactNode, title = 'Register this machine') => (
    <Stack sx={{ justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: '90vh', px: 2 }}>
      {/* ⚠ The PAGE scrolls, not a box inside it. On a phone the offline-name choice and the
          approve button sit below the fold, and a card with its own scroll bar hid them behind a
          scroll the reader had no reason to look for. */}
      <Modal open inline maxWidth="xs" color="primary.main" disableOverflow sx={{ maxHeight: 'none' }}>
        <DialogContent>
          <Stack sx={{ gap: 3, py: 1 }}>
            <Typography variant="subtitle1" color="primary">
              {title}
            </Typography>
            {children}
          </Stack>
        </DialogContent>
      </Modal>
    </Stack>
  )

  const toNodes = (
    <Button component={Link} to="/nodes" variant="contained" color="primary" size="large">
      Go to your nodes
    </Button>
  )

  const spinner = (label: string) => (
    <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
      <CircularProgress size={20} />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
    </Stack>
  )

  if (!validLink)
    return frame(
      <>
        <Alert severity="error">This is not a registration link.</Alert>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Open the link the installer printed on the machine, or scan its QR code again.
        </Typography>
        {toNodes}
      </>
    )

  if (!initialized) return frame(spinner('Loading…'))
  // `needsSignIn` covers every case below that has no session the router takes.

  // ── before sign-in: the landing card ──────────────────────────────────────
  if (needsSignIn && sessionId) {
    if (!preview) return frame(spinner('Reading the registration…'))
    if (preview.kind === 'expired')
      return frame(<Alert severity="warning">{expiredMessage()}</Alert>)
    const hostname = preview.kind === 'facts' ? preview.hostname : null
    const platform = preview.kind === 'facts' ? preview.platform : null
    return frame(
      <>
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
          <LuMonitor />
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
              {landingTitle(hostname)}
            </Typography>
            {!!platform && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {platform}
              </Typography>
            )}
          </Stack>
        </Stack>
        {bounceSpent(sessionId) ? (
          <>
            {/* A return from the router with no session: the sign-in did not stick. */}
            <Alert severity="warning">Signing in did not finish.</Alert>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Your browser may be blocking cookies for this site. Try again, or go back to the machine
              and finish the installation there.
            </Typography>
          </>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {stale
              ? 'Your sign-in has expired. Sign in again to choose its network and its name, and to approve it.'
              : 'Sign in to choose its network and its name, and to approve it.'}
          </Typography>
        )}
        <Button variant="contained" color="primary" size="large" onClick={signIn}>
          Sign in to continue
        </Button>
      </>,
      'New noBGP node'
    )
  }

  if (denied)
    return frame(
      <Alert severity="info" icon={<LuCircleStop />}>
        Registration denied. Nothing was sent to the machine. You can close this tab.
      </Alert>
    )

  // The machine stopped waiting — a key pasted into its terminal, Ctrl-C, or its own wait ran out
  // (router#1609). FINAL, from a read that says `cancelled` or from the approval's 410: one card,
  // no approve form, and a way out. Not an error: the reader did nothing wrong.
  if (phase === 'cancelled' || failure?.kind === 'cancelled')
    return frame(
      <>
        {/* ⚠ A stop icon, not the theme's info icon: this card is FINAL, and it says that the
            machine stopped waiting. */}
        <Alert severity="info" icon={<LuCircleStop />}>
          {CANCELLED_MESSAGE}
        </Alert>
        {toNodes}
      </>
    )

  if (terminal)
    return frame(
      <>
        <Alert severity={failure?.kind === 'expired' ? 'warning' : 'error'}>{failure?.message}</Alert>
        {toNodes}
      </>
    )

  if (phase === 'loading' || phase === 'done' || !record)
    return frame(
      <>
        {spinner(
          phase === 'done'
            ? `Waiting for ${record?.nodeName || 'the machine'} to come online…`
            : 'Reading the registration…'
        )}
        {/* ⚠ The FIRST read is the one that fails on a phone that has just lost its signal, and
            it is the one state with no record to draw. Without this the page spins with nothing
            to read, which is the one thing it is built not to do.
            ⚠ Not when the machine is registered (`done`): the poll has stopped, and a read that
            was still in flight can fail after it. Nothing is tried again then. */}
        {failure?.message && phase !== 'done' && <ReadNotice failure={failure} />}
      </>
    )

  const machine = machineLine(record.platform, record.agentVersion)
  const place = locationLine(record.city, record.country)
  const shown = offered ?? record
  const joins = selectedNetworkName(shown, selection) ?? plannedNetwork(record)

  const facts = (
    <Stack sx={{ gap: 1.5 }}>
      <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
        <LuMonitor />
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
            {record.hostname || 'This machine'}
          </Typography>
          {!!machine && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {machine}
            </Typography>
          )}
        </Stack>
      </Stack>
      {!!record.ip && (
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
          <LuGlobe />
          <Typography variant="code" sx={{ wordBreak: 'break-all' }}>
            {record.ip}
          </Typography>
        </Stack>
      )}
      {!!place && (
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
          <LuMapPin />
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2">{place}</Typography>
            {/* An address gives a place and never a location. A VPN or a cloud region says
                something confusing, so the page says it is a guess rather than a fact. */}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Approximate, from the address above
            </Typography>
          </Stack>
        </Stack>
      )}
    </Stack>
  )

  // ── after the approval: the wait ───────────────────────────────────────────
  if (phase === 'waiting' || phase === 'overdue')
    return frame(
      <>
        {facts}
        <Divider />
        {spinner(
          joins
            ? `Waiting for ${record.nodeName || selection.nodeName || 'the machine'} to join ${joins}…`
            : 'Waiting for the machine to finish…'
        )}
        {phase === 'overdue' && (
          <>
            {/* ⚠ SOFT, and the poll goes on. The record answers until it expires 15 minutes after
                the machine asked; this page stops only on that 404. */}
            <Alert severity="warning">This is taking longer than expected ({waitedLabel(waitedMs)}).</Alert>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              The machine may be slow, or the installer may have stopped. Check the terminal on the
              machine. This page keeps watching until the link expires, 15 minutes after the machine
              asked.
            </Typography>
            <Button component={Link} to="/nodes" variant="text" color="primary">
              Go to your nodes
            </Button>
          </>
        )}
        {failure?.message && <ReadNotice failure={failure} />}
      </>
    )

  // ── the approval ───────────────────────────────────────────────────────────
  const ids = byId(shown)
  const org = shown.orgs.find(o => o.id === selection.orgId)
  const orgNetworks = ids ? (org?.networks ?? []) : shown.networks
  const orgSettled = !ids || !!org
  const noNetwork = orgSettled && orgNetworks.length === 0
  // The networks of this org that are not listed, and why (app#397).
  const hiddenNames = ids && org ? view?.hidden[org.id] : undefined
  const hiddenLine = hiddenNetworksLine(hiddenNames)
  const nameError = !isValidNodeName(selection.nodeName)

  const createForm = (
    <Stack sx={{ gap: 1 }}>
      <CreateRefusal message={refusal?.message} upgradeUrl={refusal?.upgradeUrl} />
      <NameForm
        lowercase
        label="New network name"
        value={newNetwork}
        placeholder="home"
        helperText={org ? `Created in ${org.name}.` : undefined}
        onChange={setNewNetwork}
      />
      <Button
        variant={noNetwork ? 'contained' : 'outlined'}
        color="primary"
        disabled={!newNetwork.trim() || !isValidNodeName(newNetwork.trim()) || creating}
        onClick={create}
      >
        {creating ? (
          <>
            Creating…
            <CircularProgress color="inherit" size={18} sx={{ ml: 1 }} />
          </>
        ) : (
          'Create network'
        )}
      </Button>
    </Stack>
  )

  return frame(
    <>
      {facts}
      <Divider />

      {showsOrgPicker(record) && (
        <TextField
          select
          fullWidth
          label="Organization"
          value={selection.orgId}
          helperText="The organization that owns this machine."
          onChange={event => {
            edited.current.org = true
            edited.current.network = false
            setRefusal(null)
            pick({ orgId: event.target.value, networkId: defaultNetworkId(shown, event.target.value) })
          }}
          slotProps={{
            select: {
              displayEmpty: true,
              renderValue: value =>
                record.orgs.find(o => o.id === value)?.name ?? (
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Choose an organization
                  </Typography>
                ),
            },
          }}
        >
          {record.orgs.map(item => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      {orgSettled &&
        (noNetwork ? (
          <>
            {/* Zero networks: lead with create. A dead picker is not a question. */}
            <Alert severity="info">
              {hiddenNames?.length
                ? noAddableNetworkLine(org?.name ?? null)
                : `${ids && org ? `${org.name} has no network yet.` : 'You have no network yet.'} Create one for this machine.`}
            </Alert>
            {hiddenLine && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {hiddenLine}
              </Typography>
            )}
            {createForm}
          </>
        ) : (
          <>
            {showsNetworkPicker(shown, selection.orgId) ? (
              <TextField
                select
                fullWidth
                label="Network"
                value={ids ? selection.networkId : selection.networkName}
                helperText="The machine joins this network."
                onChange={event => {
                  edited.current.network = true
                  pick(ids ? { networkId: event.target.value } : { networkName: event.target.value })
                }}
                slotProps={{
                  // ⚠ An empty select reads as a broken field, and it is the FIRST thing a reader
                  // with more than one network sees: nothing is picked for them, on purpose.
                  select: {
                    displayEmpty: true,
                    renderValue: value =>
                      orgNetworks.find(n => (ids ? n.id === value : n.name === value))?.name ?? (
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          Choose a network
                        </Typography>
                      ),
                  },
                }}
              >
                {orgNetworks.map(item => (
                  <MenuItem key={ids ? item.id : item.name} value={ids ? item.id : item.name}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
                <LuNetwork />
                <Stack sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Joins network
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {joins ?? 'your network'}
                  </Typography>
                </Stack>
              </Stack>
            )}
            {hiddenLine && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {hiddenLine}
              </Typography>
            )}
            {createOpen ? (
              createForm
            ) : (
              <Button
                variant="text"
                color="primary"
                startIcon={<LuPlus />}
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => setCreateOpen(true)}
              >
                Create a new network
              </Button>
            )}
          </>
        ))}

      {/* ⚠ THE NAME IS EDITABLE, AND SAYS SO (Benoit, 2026-09-17: "we could let the user change the
          name" — he did not see that he could). Shown in every approval state, zero networks
          included, prefilled from the router's `suggested_name`. */}
      <NameForm
        lowercase
        label="Node name"
        value={selection.nodeName}
        placeholder={defaultNodeName(record.suggestedName ?? record.hostname) || 'hostname'}
        helperText="What this machine will be called. You can change it. Leave blank to use its hostname."
        onChange={value => {
          edited.current.name = true
          pick({ nodeName: value })
        }}
      />

      {/* ⚠ "so the name is now …" only while the field HOLDS the suggestion. The page pre-fills once
          per name, so a reader who types the taken name back sees the notice again with the field
          on the taken name, and a sentence saying the name changed would be false. */}
      {nameView?.kind === 'unavailable' && (
        <Alert severity="warning">
          {nameView.suggested === checkName
            ? `${nameView.name} cannot be used, so the name is now ${nameView.suggested}. You can change it.`
            : nameView.suggested
              ? `${nameView.name} cannot be used. ${nameView.suggested} is free.`
              : `${nameView.name} cannot be used. Choose another name.`}
        </Alert>
      )}

      {nameView?.kind === 'online' && (
        <Alert severity="info">
          {nameView.suggested === checkName
            ? `${nameView.name} is in use by an online node in this network, so the name is now ${nameView.suggested}. You can change it.`
            : nameView.suggested
              ? `${nameView.name} is in use by an online node in this network. ${nameView.suggested} is free.`
              : `${nameView.name} is in use by an online node in this network. Choose another name.`}
        </Alert>
      )}

      {nameView?.kind === 'offline' && (
        <Stack sx={{ gap: 1 }}>
          {/* ⚠⚠ NOTHING IS PICKED FOR THE READER. Replacing gives this machine another node's id,
              storage and grants; a new name leaves that node alone. Which one is right depends on
              whether this is the same box, and only the reader knows. The approval waits. */}
          <Alert severity="warning">
            {nameView.name} belongs to a node that is offline. Choose what to do before you approve.
          </Alert>
          <RadioGroup
            value={nameView.choice}
            onChange={event => {
              const choice = event.target.value as OfflineChoice
              setOfflineChoice(choice)
              edited.current.name = true
              pick({ nodeName: choice === 'new' && nameView.suggested ? nameView.suggested : nameView.name })
            }}
          >
            <FormControlLabel
              value="replace"
              control={<Radio />}
              sx={{ alignItems: 'flex-start', mb: 1, '& .MuiRadio-root': { pt: 0.5 } }}
              label={
                <Stack>
                  <Typography variant="body1">Replace {nameView.name}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    This machine takes over that node: its id, its storage and its grants. Choose this
                    when you reinstall the same machine.
                  </Typography>
                </Stack>
              }
            />
            {nameView.suggested ? (
              <FormControlLabel
                value="new"
                control={<Radio />}
                sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
                label={
                  <Stack>
                    <Typography variant="body1">Register as {nameView.suggested}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      A new node. {nameView.name} stays as it is. Choose this for a different machine.
                    </Typography>
                  </Stack>
                }
              />
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Or type a different name above for a new node.
              </Typography>
            )}
          </RadioGroup>
        </Stack>
      )}

      {/* The new node's STARTING setting (router PERMISSIONS.md § 4.1, D-20, app#375): it follows
          its network, or the reader, who creates it, keeps it Private (you and org admins). Written
          once, onto a node this registration creates, so it is hidden for a replace, which keeps
          that node's own setting. */}
      {!replaceChosen(nameView) && (
        <FormControlLabel
          sx={{ alignItems: 'flex-start', marginLeft: 0, marginRight: 0 }}
          control={
            <Checkbox
              size="small"
              sx={{ paddingTop: 0.25 }}
              checked={selection.keepPrivate === true}
              onChange={event => pick({ keepPrivate: event.target.checked })}
            />
          }
          label={
            <>
              <Typography variant="body2">Keep this node private (you and org admins)</Typography>
              <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                {networkAccess && `This network is ${NETWORK_ACCESS_LABEL[networkAccess]}. `}
                {keepPrivateCaption(selection.keepPrivate === true, networkAccess)} You can change this later on the
                node’s page.
              </Typography>
            </>
          }
        />
      )}

      {/* ⚠⚠ THE LINK-ATTACK LOCK (router#1752): the approval lets the router give the machine a
          token, so a link that a stranger started and sent must not be approved. The page does not
          show the code again (Benoit, 2026-09-28): since router#1859 the code is how this page
          found the session, typed or carried by the QR code, so comparing it checked nothing. */}
      <Alert severity="warning" icon={false}>
        <Typography variant="body2">
          Approve only if you started nobgp register on this machine yourself. If somebody sent you
          this link, do not approve.
        </Typography>
      </Alert>

      {/* ⚠ THE BUTTON IS THE APPROVAL, and nothing happens on the machine before it: the machine
          waits for this yes. So the label says what it does to the machine. */}
      {/* Approve and Deny together, 8px apart, as on the router's sign-in page. */}
      <Stack sx={{ gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!canApprove(record, selection) || nameError || nameBlocksApproval(nameView) || sending || denying}
          onClick={send}
        >
          {sending ? (
            <>
              Registering…
              <CircularProgress color="inherit" size={18} sx={{ ml: 1 }} />
            </>
          ) : (
            'Approve and register'
          )}
        </Button>
        <Button variant="text" color="primary" size="large" disabled={sending || denying} onClick={deny}>
          {denying ? <CircularProgress color="inherit" size={18} /> : 'Deny'}
        </Button>
      </Stack>
      {sendFailure && (
        // A name that became taken is news, not an error the reader made: the notice above says what
        // to do, and this says why the first press did not go through.
        <Alert severity={sendFailure.kind === 'name_taken' ? 'warning' : 'error'}>{sendFailure.message}</Alert>
      )}
      {failure?.message && <ReadNotice failure={failure} />}
    </>
  )
}
